import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ResourceType } from '@completion/enums';
import {
  CheckSheetAssignment,
  CheckSheetAssignmentProjection,
  ProcurementPackage,
  Punch,
  User,
  UserCheckSheetAssignmentProjection,
  Vendor,
} from '@completion/models';
import { BaseService, Resource } from './base.service';
import { UserChecksheetAssignment } from '../../shared/models/user-checksheet-assignment';

@Injectable({
  providedIn: 'root',
})
export class SupplierService extends BaseService {
  private readonly url = this.baseUrl + '/';
  private readonly projectUrl = this.baseUrl + '/projects';

  constructor(readonly http: HttpClient) {
    super(http);
  }

  @Resource(ResourceType.GetSuppliers)
  getSuppliers(projectId: number): Observable<User[]> {
    return this.http.get<User[]>(`${this.projectUrl}/${projectId}/supplier/suppliers`);
  }

  @Resource(ResourceType.GetProcurementPackages)
  getProcurementPackages(projectId: number): Observable<ProcurementPackage[]> {
    return this.http.get<ProcurementPackage[]>(`${this.projectUrl}/${projectId}/supplier/procurementPackages`);
  }

  @Resource(ResourceType.GetSupplierCheckSheetAssignments)
  getSupplierCheckSheetAssignments(projectId: number, procurementPackageNos: ProcurementPackage[]): Observable<CheckSheetAssignment[]> {
    return this.http.post<CheckSheetAssignment[]>(
      `${this.projectUrl}/${projectId}/supplier/procurementPackages/sow`,
      procurementPackageNos,
    );
  }

  @Resource(ResourceType.GetSupplierCheckSheetAssignments)
  getLineTagCheckSheetAssignments(projectId: number): Observable<CheckSheetAssignment[]> {
    return this.http.get<CheckSheetAssignment[]>(`${this.projectUrl}/${projectId}/supplier/lineTags/sow`);
  }

  @Resource(ResourceType.LoadVendors)
  getVendors(projectId: number): Observable<Vendor[]> {
    return this.http.get<Vendor[]>(`${this.projectUrl}/${projectId}/supplier/vendors`);
  }

  @Resource(ResourceType.UpdateVendor)
  updateVendor(projectId: number, vendor: Vendor): Observable<Vendor[]> {
    return this.http.put<Vendor[]>(`${this.projectUrl}/${projectId}/supplier/vendors`, vendor);
  }

  @Resource(ResourceType.LoadVendorUsersForPackages)
  getVendorUsersForPackages(projectId: number, procurementPackageIds: number[]): Observable<User[]> {
    return this.http.post<User[]>(`${this.projectUrl}/${projectId}/supplier/vendors/users`, procurementPackageIds);
  }

  @Resource(ResourceType.SaveUserCheckSheetAssignments)
  saveUserCheckSheetAssignments(projectId: number, userCheckSheetAssignments: UserChecksheetAssignment[]): Observable<void> {
    return this.http.post<void>(`${this.projectUrl}/${projectId}/supplier/checksheet/assignments`, userCheckSheetAssignments);
  }

  @Resource(ResourceType.GetAccessibleSheetAssignmentIds)
  getAccessibleCheckSheetAssignmentIds(projectId: number): Observable<number[]> {
    return this.http.get<number[]>(`${this.projectUrl}/${projectId}/supplier/checksheet/accessibleAssignments`);
  }

  @Resource(ResourceType.SendTransmittal)
  sendTransmittal(projectId: number, userChecksheetAssignments: UserChecksheetAssignment[]): Observable<void> {
    return this.http.post<void>(`${this.projectUrl}/${projectId}/supplier/sendTransmittal`, userChecksheetAssignments);
  }

  @Resource(ResourceType.GetCurrentUserScope)
  getCurrentUserScope(projectId: number): Observable<CheckSheetAssignment[]> {
    return this.http.get<CheckSheetAssignment[]>(`${this.projectUrl}/${projectId}/supplier/currentUserScope`);
  }
  @Resource(ResourceType.GetCurrentUserScope)
  getScopeForUser(projectId: number, userId: number): Observable<CheckSheetAssignment[]> {
    return this.http.get<CheckSheetAssignment[]>(`${this.projectUrl}/${projectId}/supplier/currentUserScope/${userId}`);
  }

  @Resource(ResourceType.GetScopeRelatedPunches)
  getScopeRelatedPunches(projectId: number): Observable<Punch[]> {
    return this.http.get<Punch[]>(`${this.projectUrl}/${projectId}/supplier/scopeRelatedPunches`);
  }

  @Resource(ResourceType.GetUsersWithOpenAssignments)
  getUsersWithOpenAssignments(projectId: number): Observable<User[]> {
    return this.http.get<User[]>(`${this.projectUrl}/${projectId}/supplier/usersWithOpenAssignments`);
  }

  @Resource(ResourceType.GetOpenAssignmentsForUser)
  getOpenAssignmentsForUser(projectId: number, userId: number): Observable<CheckSheetAssignmentProjection[]> {
    return this.http.get<CheckSheetAssignmentProjection[]>(`${this.projectUrl}/${projectId}/supplier/openUserScope/${userId}`);
  }

  @Resource(ResourceType.UnlinkUserCheckSheetAssignments)
  unlinkUsersAndCheckSheetAssignments(
    projectId: number,
    userChecksheetAssignments: UserCheckSheetAssignmentProjection[],
  ): Observable<void> {
    return this.http.post<void>(`${this.projectUrl}/${projectId}/supplier/unlinkCheckSheetAssignments`, userChecksheetAssignments);
  }
}
